import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { AuthContext } from './AuthContext';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('tbt_token'));
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authView, setAuthView] = useState('login'); 
  const [permissions, setPermissions] = useState(JSON.parse(localStorage.getItem('user_permissions')) || []);

  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem('tbt_token');
    localStorage.removeItem('user_permissions');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setPermissions([]);
    setAuthView('login');
    setError(null);
    navigate('/');
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('tbt_token');
      if (storedToken) {
        // INYECCIÓN CRÍTICA: Mantiene los headers vivos al recargar la página
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        try {
          const response = await api.get('/user');
          const userData = response.data?.user || response.data;
          if (userData && (userData.id || userData.email)) {
            setUser(userData);
            setPermissions(userData.permissions || JSON.parse(localStorage.getItem('user_permissions')) || []);
          } else {
            logout();
          }
        } catch (err) {
          console.error("Token expirado o inválido:", err);
          logout();
        }
      }
      setIsVerifying(false);
    };
    initAuth();
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/login', credentials);
      const resToken = response.data.token || response.data.data?.token;
      const resUser = response.data.user || response.data.data?.user;
      const resPermissions = resUser.permissions || []; 

      if (resToken && resUser) {
        localStorage.setItem('tbt_token', resToken);
        localStorage.setItem('user_permissions', JSON.stringify(resPermissions));
        
        api.defaults.headers.common['Authorization'] = `Bearer ${resToken}`;
        
        setToken(resToken);
        setUser(resUser);
        setPermissions(resPermissions); 
        setIsAuthModalOpen(false);
        setError(null);
        
        return { success: true, user: resUser, permissions: resPermissions };
      }
      setError("El servidor no envió los datos completos.");
      return { success: false };
    } catch (err) {
      if (err.response?.data?.requires_verification) {
        return {
          success: false,
          requiresVerification: true,
          email: err.response.data.email || credentials.email,
          message: err.response.data.message
        };
      }
      setError(err.response?.data?.message || 'Credenciales inválidas');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const register = async (formData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/register', formData);
      const resToken = response.data.token;
      const resUser = response.data.user;

      if (response.data) {
        setError(null);
        return { success: true, user: resUser, email: formData.email };
      }
      return { success: false };
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      if (validationErrors) {
        const firstError = Object.values(validationErrors).flat()[0];
        setError(firstError);
      } else {
        setError(err.response?.data?.message || 'Error al crear la cuenta');
      }
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const completeSession = (resToken, resUser) => {
    const resPermissions = resUser?.permissions || [];
    if (resToken && resUser) {
      localStorage.setItem('tbt_token', resToken);
      localStorage.setItem('user_permissions', JSON.stringify(resPermissions));
      api.defaults.headers.common['Authorization'] = `Bearer ${resToken}`;
      setToken(resToken);
      setUser(resUser);
      setPermissions(resPermissions);
      setIsAuthModalOpen(false);
      setError(null);
    }
  };

  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/forgot-password', { email });
      return { success: true, message: response.data.message };
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al solicitar la recuperación de contraseña';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async ({ email, code, password, password_confirmation }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/reset-password', { email, code, password, password_confirmation });
      return { success: true, message: response.data.message };
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      const msg = validationErrors ? Object.values(validationErrors).flat()[0] : (err.response?.data?.message || 'Error al restablecer la contraseña');
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  const changeAuthView = (newView) => {
    setError(null);
    setAuthView(newView);
  };

  const value = {
    user, token, permissions, loading, error, isVerifying, isAuthModalOpen, authView,
    setAuthView: changeAuthView, clearError: () => setError(null), login, register, logout, completeSession, forgotPassword, resetPassword,
    openAuthModal: (initialView = 'login') => {
      setError(null);
      setAuthView(initialView);
      setIsAuthModalOpen(true);
    },
    closeAuthModal: () => {
      setIsAuthModalOpen(false);
      setAuthView('login');
      setError(null);
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children} 
    </AuthContext.Provider>
  );
};